import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useBalances } from '../hooks/useBalances';
import useSWR from 'swr';
import apiClient from '../services/apiClient';
import { Copy, RefreshCw, AlertCircle, Wallet } from 'lucide-react';
import './TransferForm.css'; // Reuse the same styles

const swrFetcher = (url) => apiClient.get(url).then(res => res.data);

const ASSETS = [
  { id: 'USDT', name: 'USDT', decimals: 6 },
  { id: 'USDC', name: 'USDC', decimals: 6 },
  { id: 'ETH', name: 'ETH', decimals: 18 },
];

const CHAINS = [
  { id: 'arbitrum', name: 'Arbitrum' },
  { id: 'ethereum', name: 'Ethereum' },
];

const formatBalance = (value) => {
  if (value === undefined || value === null) return '0.00';
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 });
};

// Helper to shorten address
const shortenAddress = (addr, start = 6, end = 4) => {
  if (!addr || addr.length < start + end + 3) return addr;
  return `${addr.slice(0, start)}...${addr.slice(-end)}`;
};

// Copy to clipboard helper
const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {
    console.log('Address copied to clipboard');
  }).catch(err => {
    console.error('Failed to copy:', err);
  });
};

function SwapForm() {
  const { balances, refresh: refreshBalances, isValidating: isRefreshingBalances } = useBalances(10000);
  
  // Fetch managed wallets (wallets with private keys)
  const { data: walletsData, mutate: refreshWallets } = useSWR('/swaps/wallets', swrFetcher, {
    refreshInterval: 0,
    revalidateOnFocus: true,
  });

  const [formData, setFormData] = useState({
    walletId: '',
    sourceAsset: 'USDT',
    destinationAsset: 'USDC',
    amount: '',
    chain: 'arbitrum',
    slippageTolerance: 0.5, // 0.5%
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [quote, setQuote] = useState(null);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Set default wallet when wallets load
  useEffect(() => {
    if (!formData.walletId && walletsData?.wallets?.length > 0) {
      setFormData(prev => ({ ...prev, walletId: walletsData.wallets[0].id }));
    }
  }, [walletsData, formData.walletId]);

  // Group wallets by address to show only unique wallets (no chain suffix in dropdown)
  const walletsList = useMemo(() => {
    if (!walletsData?.wallets) return [];
    
    // Group by address to get unique wallets
    const walletsMap = new Map();
    walletsData.wallets.forEach(wallet => {
      const addr = wallet.address.toLowerCase();
      if (!walletsMap.has(addr)) {
        walletsMap.set(addr, {
          ...wallet,
          // Store all chain IDs this wallet supports
          chains: [wallet.chain],
        });
      } else {
        // Add chain to existing wallet if not already present
        const existing = walletsMap.get(addr);
        if (!existing.chains.includes(wallet.chain)) {
          existing.chains.push(wallet.chain);
        }
      }
    });
    
    // Return only unique wallets (by address)
    return Array.from(walletsMap.values());
  }, [walletsData]);

  // Find the actual wallet that matches the selected wallet address AND the selected chain
  const selectedWallet = useMemo(() => {
    if (!formData.walletId || !walletsData?.wallets) return null;
    
    // Find the wallet from the grouped list
    const groupedWallet = walletsList.find(w => w.id === formData.walletId);
    if (!groupedWallet) return null;
    
    // Find the actual wallet that matches both address and selected chain
    const actualWallet = walletsData.wallets.find(
      w => w.address.toLowerCase() === groupedWallet.address.toLowerCase() && 
           w.chain === formData.chain
    );
    
    // If no wallet found for selected chain, return the first one (will need to handle chain mismatch)
    return actualWallet || walletsData.wallets.find(
      w => w.address.toLowerCase() === groupedWallet.address.toLowerCase()
    );
  }, [walletsList, formData.walletId, formData.chain, walletsData]);

  // Get the correct wallet ID for the selected chain
  const getWalletIdForChain = useMemo(() => {
    if (!selectedWallet) return formData.walletId;
    // Return the wallet ID that matches the selected chain
    const walletOnChain = walletsData?.wallets?.find(
      w => w.address.toLowerCase() === selectedWallet.address.toLowerCase() && 
           w.chain === formData.chain
    );
    return walletOnChain?.id || formData.walletId;
  }, [selectedWallet, formData.chain, formData.walletId, walletsData]);

  // Get wallet balance for selected asset
  const walletBalance = useMemo(() => {
    if (!selectedWallet || !balances?.treasury) return null;
    
    // Find the balance key that matches the wallet address and asset
    const balanceKey = Object.keys(balances.treasury).find(key => 
      key.toLowerCase().includes(selectedWallet.address.toLowerCase()) && 
      key.toLowerCase().includes(formData.sourceAsset.toLowerCase())
    );
    
    if (!balanceKey) return null;
    return balances.treasury[balanceKey];
  }, [selectedWallet, balances, formData.sourceAsset]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // When sourceAsset changes, if it matches destinationAsset, flip destinationAsset to the other token
    if (name === 'sourceAsset') {
      if (value === formData.destinationAsset) {
        // Prefer USDT/USDC swap: if selecting USDC, flip to USDT, and vice versa
        let otherAsset;
        if (value === 'USDC') {
          otherAsset = ASSETS.find(a => a.id === 'USDT');
        } else if (value === 'USDT') {
          otherAsset = ASSETS.find(a => a.id === 'USDC');
        } else {
          // For ETH, prefer USDC
          otherAsset = ASSETS.find(a => a.id === 'USDC');
        }
        setFormData((prev) => ({ 
          ...prev, 
          [name]: value,
          destinationAsset: otherAsset ? otherAsset.id : ''
        }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    }
    // When destinationAsset changes, if it matches sourceAsset, flip sourceAsset to the other token
    else if (name === 'destinationAsset') {
      if (value === formData.sourceAsset) {
        // Prefer USDT/USDC swap: if selecting USDC, flip to USDT, and vice versa
        let otherAsset;
        if (value === 'USDC') {
          otherAsset = ASSETS.find(a => a.id === 'USDT');
        } else if (value === 'USDT') {
          otherAsset = ASSETS.find(a => a.id === 'USDC');
        } else {
          // For ETH, prefer USDC
          otherAsset = ASSETS.find(a => a.id === 'USDC');
        }
        setFormData((prev) => ({ 
          ...prev, 
          [name]: value,
          sourceAsset: otherAsset ? otherAsset.id : ''
        }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    }
    // When chain changes, we need to find the wallet ID that matches the selected wallet address and new chain
    else if (name === 'chain' && formData.walletId && walletsData?.wallets) {
      const currentWallet = walletsList.find(w => w.id === formData.walletId);
      if (currentWallet) {
        // Find wallet with same address but on the new chain
        const walletOnNewChain = walletsData.wallets.find(
          w => w.address.toLowerCase() === currentWallet.address.toLowerCase() && 
               w.chain === value
        );
        if (walletOnNewChain) {
          setFormData((prev) => ({ ...prev, [name]: value, walletId: walletOnNewChain.id }));
        } else {
          setFormData((prev) => ({ ...prev, [name]: value }));
        }
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
    if (error) setError(null);
    if (quoteError) setQuoteError(null);
    // Clear quote when any parameter changes
    setQuote(null);
  };

  const handleSwapAssets = () => {
    setFormData((prev) => ({
      ...prev,
      sourceAsset: prev.destinationAsset,
      destinationAsset: prev.sourceAsset,
    }));
    setQuote(null);
    setQuoteError(null);
  };

  const handleSetMax = () => {
    if (walletBalance?.available) {
      setFormData((prev) => ({ ...prev, amount: walletBalance.available.toString() }));
    }
  };

  const getQuote = useCallback(async () => {
    const walletId = getWalletIdForChain;
    if (!walletId || !formData.amount || parseFloat(formData.amount) <= 0) {
      return;
    }

    setIsLoadingQuote(true);
    setQuoteError(null);
    setQuote(null);

    try {
      const response = await apiClient.post('/swaps/quote', {
        walletId: walletId,
        sourceAsset: formData.sourceAsset,
        destinationAsset: formData.destinationAsset,
        amountIn: formData.amount,
        chain: formData.chain,
      });
      setQuote(response.data);
    } catch (err) {
      console.error('Failed to get quote:', err);
      setQuoteError(err.response?.data?.error || 'Failed to get quote');
    } finally {
      setIsLoadingQuote(false);
    }
  }, [getWalletIdForChain, formData.amount, formData.sourceAsset, formData.destinationAsset, formData.chain]);

  // Auto-fetch quote when amount or assets change (with debounce)
  useEffect(() => {
    if (formData.amount && parseFloat(formData.amount) > 0 && getWalletIdForChain) {
      const timer = setTimeout(() => {
        getQuote();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formData.amount, formData.sourceAsset, formData.destinationAsset, getWalletIdForChain, formData.chain, getQuote]);

  const validateForm = () => {
    const errors = {};
    if (!formData.walletId) errors.walletId = 'Required';
    if (formData.sourceAsset === formData.destinationAsset) {
      errors.destinationAsset = 'Must differ from source';
    }
    const amount = parseFloat(formData.amount);
    if (!formData.amount || amount <= 0) errors.amount = 'Invalid';
    else if (walletBalance && amount > parseFloat(walletBalance.available)) {
      errors.amount = 'Insufficient';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) setShowConfirmation(true);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    
    const walletId = getWalletIdForChain;
    if (!walletId) {
      setError('No wallet found for selected chain');
      setIsSubmitting(false);
      return;
    }
    
    try {
      const response = await apiClient.post('/swaps/execute', {
        walletId: walletId,
        sourceAsset: formData.sourceAsset,
        destinationAsset: formData.destinationAsset,
        amountIn: formData.amount,
        chain: formData.chain,
        slippageTolerance: formData.slippageTolerance,
      });
      
      // Show success message
      setSuccessMessage({
        txHash: response.data.txHash,
        amountIn: response.data.amountIn,
        expectedAmountOut: response.data.expectedAmountOut,
        sourceAsset: response.data.sourceAsset,
        destinationAsset: response.data.destinationAsset,
      });
      
      // Reset form
      setFormData({
        walletId: formData.walletId, // Keep selected wallet
        sourceAsset: 'USDT',
        destinationAsset: 'USDC',
        amount: '',
        chain: formData.chain, // Keep selected chain
        slippageTolerance: 0.5,
      });
      setShowConfirmation(false);
      setQuote(null);
    } catch (err) {
      console.error('Failed to execute swap:', err);
      setError(err.response?.data?.error || 'Failed to execute swap');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="transfer-form-container">
      <div className="form-header">
        <h3>Swap Assets</h3>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)} className="dismiss-btn">×</button>
        </div>
      )}

      {successMessage && (
        <div className="success-banner" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px',
          background: 'rgba(16, 185, 129, 0.1)',
          borderBottom: '1px solid rgba(16, 185, 129, 0.2)',
          color: '#10b981',
          fontSize: '0.85rem',
          marginBottom: '20px',
        }}>
          <div>
            <div style={{ fontWeight: '500', marginBottom: '4px' }}>Swap successful!</div>
            <div style={{ fontSize: '0.75rem', color: '#6ee7b7' }}>
              {successMessage.amountIn} {successMessage.sourceAsset} → {successMessage.expectedAmountOut} {successMessage.destinationAsset}
            </div>
            <a 
              href={`https://${formData.chain === 'arbitrum' ? 'arbiscan.io' : 'etherscan.io'}/tx/${successMessage.txHash}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '0.7rem', color: '#10b981', textDecoration: 'underline' }}
            >
              View transaction
            </a>
          </div>
          <button onClick={() => setSuccessMessage(null)} className="dismiss-btn" style={{ color: '#10b981' }}>×</button>
        </div>
      )}

      <form onSubmit={handlePreSubmit}>
        {/* Wallet Selection */}
        <div className="transfer-section">
          <div className="section-label">From Wallet</div>
          <div className="form-field">
            <select
              name="walletId"
              value={formData.walletId}
              onChange={handleChange}
              className={validationErrors.walletId ? 'error' : ''}
              disabled={isSubmitting}
            >
              <option value="">Select wallet...</option>
              {walletsList.map(wallet => (
                <option key={wallet.id} value={wallet.id}>
                  {wallet.label || shortenAddress(wallet.address)}
                </option>
              ))}
            </select>
            {validationErrors.walletId && <span className="field-error">{validationErrors.walletId}</span>}
          </div>

          {selectedWallet && (
            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>
                {shortenAddress(selectedWallet.address, 8, 8)}
              </span>
              <button
                type="button"
                onClick={() => copyToClipboard(selectedWallet.address)}
                className="btn-copy-address"
                title="Copy address"
              >
                <Copy size={12} />
              </button>
            </div>
          )}
        </div>

        {/* Chain Selection */}
        <div className="transfer-section">
          <div className="section-label">Network</div>
          <div className="network-cards">
            {CHAINS.map(chain => (
              <button
                key={chain.id}
                type="button"
                className={`network-card ${formData.chain === chain.id ? 'active' : ''}`}
                onClick={() => handleChange({ target: { name: 'chain', value: chain.id } })}
                disabled={isSubmitting}
              >
                <div className="network-name">{chain.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Swap Assets Section */}
        <div className="transfer-section">
          <div className="section-label">Swap</div>
          
          {/* From Asset */}
          <div className="asset-amount-row" style={{ marginBottom: '12px' }}>
            <div className="form-field asset-field">
              <label>From</label>
              <select
                name="sourceAsset"
                value={formData.sourceAsset}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                {ASSETS.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              {walletBalance && selectedWallet && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  marginTop: '6px',
                  fontSize: '0.75rem',
                  color: '#888',
                }}>
                  <Wallet size={14} style={{ color: '#666', opacity: 0.6 }} />
                  <span>{formatBalance(walletBalance.available)}</span>
                  <button
                    type="button"
                    onClick={() => refreshBalances()}
                    disabled={isRefreshingBalances || isSubmitting}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: '2px',
                      cursor: isRefreshingBalances || isSubmitting ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      opacity: isRefreshingBalances || isSubmitting ? 0.5 : 0.7,
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isRefreshingBalances && !isSubmitting) {
                        e.currentTarget.style.opacity = '1';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = isRefreshingBalances || isSubmitting ? '0.5' : '0.7';
                    }}
                    title="Refresh balance"
                  >
                    <RefreshCw 
                      size={12} 
                      style={{ 
                        color: '#666',
                        animation: isRefreshingBalances ? 'spin 1s linear infinite' : 'none',
                      }} 
                    />
                  </button>
                </div>
              )}
            </div>

            <div className="form-field amount-field">
              <label>
                Amount
                {walletBalance && parseFloat(walletBalance.available) > 0 && (
                  <button type="button" className="max-btn" onClick={handleSetMax} disabled={isSubmitting}
                    style={{
                      marginLeft: '8px',
                      padding: '2px 8px',
                      fontSize: '0.7rem',
                      background: '#2a2a2a',
                      border: '1px solid #333',
                      borderRadius: '3px',
                      color: '#aaa',
                      cursor: 'pointer',
                    }}
                  >
                    MAX
                  </button>
                )}
              </label>
              <div className="amount-input-wrapper">
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className={validationErrors.amount ? 'error' : ''}
                  disabled={isSubmitting}
                />
                <span className="amount-unit">{formData.sourceAsset}</span>
              </div>
              {validationErrors.amount && <span className="field-error">{validationErrors.amount}</span>}
            </div>
          </div>

          {/* Swap Button */}
          <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
            <button
              type="button"
              onClick={handleSwapAssets}
              disabled={isSubmitting}
              style={{
                padding: '8px',
                background: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '6px',
                color: '#999',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.background = '#2a2a2a';
                  e.currentTarget.style.borderColor = '#555';
                  e.currentTarget.style.color = '#fff';
                  e.currentTarget.style.transform = 'rotate(180deg)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = '#1a1a1a';
                e.currentTarget.style.borderColor = '#333';
                e.currentTarget.style.color = '#999';
                e.currentTarget.style.transform = 'rotate(0deg)';
              }}
            >
              <RefreshCw size={16} />
            </button>
          </div>

          {/* To Asset */}
          <div className="form-field asset-field">
            <label>To</label>
            <select
              name="destinationAsset"
              value={formData.destinationAsset}
              onChange={handleChange}
              disabled={isSubmitting}
              className={validationErrors.destinationAsset ? 'error' : ''}
            >
              {ASSETS.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            {validationErrors.destinationAsset && <span className="field-error">{validationErrors.destinationAsset}</span>}
          </div>

          {/* Quote Display */}
          {isLoadingQuote && (
            <div style={{ 
              marginTop: '12px', 
              padding: '12px', 
              background: '#0a0a0a', 
              border: '1px solid #2a2a2a',
              borderRadius: '4px',
              fontSize: '0.85rem',
              color: '#888',
              textAlign: 'center',
            }}>
              Getting quote...
            </div>
          )}

          {quoteError && (
            <div style={{ 
              marginTop: '12px', 
              padding: '12px', 
              background: 'rgba(239, 68, 68, 0.1)', 
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '4px',
              fontSize: '0.85rem',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <AlertCircle size={16} />
              {quoteError}
            </div>
          )}

          {quote && !isLoadingQuote && (
            <div style={{ 
              marginTop: '12px', 
              padding: '12px', 
              background: '#0a0a0a', 
              border: '1px solid #2a2a2a',
              borderRadius: '4px',
            }}>
              <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '8px', textTransform: 'uppercase' }}>
                Estimated Output
              </div>
              <div style={{ fontSize: '1.1rem', color: '#10b981', fontWeight: '600', marginBottom: '8px' }}>
                {formatBalance(quote.amountOut)} {formData.destinationAsset}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#666', display: 'flex', justifyContent: 'space-between' }}>
                <span>Rate:</span>
                <span>1 {formData.sourceAsset} = {quote.exchangeRate} {formData.destinationAsset}</span>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#666', display: 'flex', justifyContent: 'space-between' }}>
                <span>Price Impact:</span>
                <span>{(quote.priceImpact * 100).toFixed(2)}%</span>
              </div>
            </div>
          )}
        </div>

        {/* Slippage Tolerance */}
        <div className="transfer-section">
          <div className="section-label">Slippage Tolerance</div>
          <div className="form-field">
            <div style={{ display: 'flex', gap: '8px' }}>
              {[0.5, 1.0, 2.0].map(value => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, slippageTolerance: value }))}
                  disabled={isSubmitting}
                  style={{
                    flex: 1,
                    padding: '8px',
                    background: formData.slippageTolerance === value ? '#2a2a2a' : '#0a0a0a',
                    border: formData.slippageTolerance === value ? '1px solid #444' : '1px solid #333',
                    borderRadius: '4px',
                    color: formData.slippageTolerance === value ? '#fff' : '#888',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                  }}
                >
                  {value}%
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-btn" disabled={isSubmitting || !quote}>
            {isSubmitting ? 'Processing...' : 'Swap'}
          </button>
        </div>
      </form>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="modal-overlay" onClick={() => setShowConfirmation(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h4>Confirm Swap</h4>
            
            <table className="confirm-table">
              <tbody>
                <tr>
                  <td className="label">Wallet</td>
                  <td className="mono">{shortenAddress(selectedWallet?.address, 8, 6)}</td>
                </tr>
                <tr>
                  <td className="label">Network</td>
                  <td>{CHAINS.find(c => c.id === formData.chain)?.name}</td>
                </tr>
                <tr className="highlight">
                  <td className="label">From</td>
                  <td className="mono">{formData.amount} {formData.sourceAsset}</td>
                </tr>
                <tr className="highlight">
                  <td className="label">To (Est.)</td>
                  <td className="mono" style={{ color: '#10b981' }}>
                    {quote ? formatBalance(quote.amountOut) : '...'} {formData.destinationAsset}
                  </td>
                </tr>
                <tr>
                  <td className="label">Slippage</td>
                  <td>{formData.slippageTolerance}%</td>
                </tr>
                {quote && (
                  <tr>
                    <td className="label">Min. Received</td>
                    <td className="mono">
                      {formatBalance(parseFloat(quote.amountOut) * (1 - formData.slippageTolerance / 100))} {formData.destinationAsset}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowConfirmation(false)} disabled={isSubmitting}>
                Cancel
              </button>
              <button className="confirm-btn" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Processing...' : 'Confirm Swap'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SwapForm;
